/**
 * Forgot password — pre-shell page at `/forgot-password`. Ports the Flutter
 * `forgot_password_page.dart`: an email field that requests a reset link via
 * `useAuth().requestPasswordReset`, then swaps to a "check your inbox"
 * confirmation. Links back to `/login`. Token-driven; no raw hex.
 */
import { useCallback, useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { MailCheck } from 'lucide-react'
import { useAuth } from '@/app/auth-context'
import { Button, Input } from '@/components/ui'
import { AuthErrorAlert, AuthPaneHeader, AuthShell } from './auth-shell'

function validateEmail(v: string): string | undefined {
  if (!v.trim()) return 'Email is required'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) {
    return 'Enter a valid email address (e.g. you@company.com)'
  }
  return undefined
}

export const ForgotPasswordPage = function ForgotPasswordPage() {
  const { requestPasswordReset } = useAuth()
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  const emailError = touched ? validateEmail(email) : undefined

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (submitting) return
      setTouched(true)
      if (validateEmail(email)) return
      setFormError(null)
      setSubmitting(true)
      requestPasswordReset(email.trim())
        .then(() => setSent(true))
        .catch((err: unknown) => {
          setFormError(err instanceof Error ? err.message : `Couldn't send the reset link`)
        })
        .finally(() => setSubmitting(false))
    },
    [submitting, email, requestPasswordReset],
  )

  if (sent) {
    return (
      <AuthShell>
        <ConfirmationView email={email.trim()} />
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <AuthPaneHeader
        title="Forgot password"
        subtitle="Enter your email address and we'll send you a reset link."
      />
      <form
        onSubmit={onSubmit}
        noValidate
        style={{ marginTop: 'var(--s-6)', display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}
      >
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          icon="user"
          placeholder="you@company.com"
          value={email}
          error={emailError}
          onChange={(e) => setEmail(e.currentTarget.value)}
          disabled={submitting}
        />
        {formError && <AuthErrorAlert message={formError} />}
        <Button type="submit" size="lg" loading={submitting} disabled={submitting}>
          {submitting ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
      <p style={{ marginTop: 'var(--s-4)', fontSize: 'var(--fs-13)', color: 'var(--text-mute)' }}>
        <Link to="/login" style={{ color: 'var(--link)', fontWeight: 'var(--fw-medium)' }}>
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  )
}

function ConfirmationView({ email }: { email: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: 'var(--r-pill)',
          background: 'var(--ok-100)',
          color: 'var(--ok-700)',
        }}
      >
        <MailCheck size={22} strokeWidth={1.75} />
      </span>
      <AuthPaneHeader
        title="Check your inbox"
        subtitle={`If an account exists for ${email}, a password reset link is on its way. Please check your inbox.`}
      />
      <p style={{ fontSize: 'var(--fs-13)', color: 'var(--text-mute)' }}>
        <Link to="/login" style={{ color: 'var(--link)', fontWeight: 'var(--fw-medium)' }}>
          Back to sign in
        </Link>
      </p>
    </div>
  )
}
